import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ExternalLink, Star, Sparkles } from 'lucide-react';
import ToolCard from '../components/ToolCard';
import { API } from '../api/client';

export default function AlternativesPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setData(null);
    setErr(null);
    axios
      .get(`${API}/resources/${slug}/alternatives`, { params: { limit: 24 } })
      .then((r) => setData(r.data))
      .catch(() => setErr('This tool does not exist or has no alternatives yet.'));
  }, [slug]);

  useEffect(() => {
    if (!data?.source) return;
    const title = `Best ${data.source.name} Alternatives (2026) — AI Framework`;
    document.title = title;
    const desc = `Discover ${data.count} verified alternatives to ${data.source.name} in ${data.source.primary_category}. Compare features, pricing, and pick the right AI tool.`;
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement('meta'); m.setAttribute('name', 'description'); document.head.appendChild(m); }
    m.setAttribute('content', desc);
  }, [data]);

  if (err) {
    return (
      <div className="page-container" data-testid="alt-error">
        <div className="empty-state">{err}</div>
        <Link to="/" className="pill-btn">Back to home</Link>
      </div>
    );
  }
  if (!data) {
    return <div className="page-container"><div className="empty-state">Loading…</div></div>;
  }

  const { source, results, count } = data;
  const initial = source.name.trim().charAt(0).toUpperCase();

  return (
    <div className="page-container" data-testid="alternatives-page">
      <div className="crumbs">
        <Link to="/">Home</Link>
        <span>/</span>
        <Link to={`/tool/${source.slug}`}>{source.name}</Link>
        <span>/</span>
        <span>Alternatives</span>
      </div>

      <header className="alt-hero" data-testid="alt-hero">
        <div className="alt-hero-logo" aria-hidden>
          {source.logo ? <img src={source.logo} alt="" /> : <span>{initial}</span>}
        </div>
        <div className="alt-hero-body">
          <div className="alt-hero-eyebrow">Alternatives</div>
          <h1 className="alt-hero-title">
            The best <span className="alt-hero-highlight">{source.name}</span> alternatives
          </h1>
          <p className="alt-hero-sub">
            {count > 0
              ? `${count} hand-picked ${source.primary_category?.toLowerCase() || 'AI'} tools that solve the same problem. Sorted by relevance and popularity.`
              : `We have not indexed alternatives to ${source.name} yet — check back soon or submit one.`}
          </p>
          <div className="alt-hero-actions">
            <Link to={`/tool/${source.slug}`} className="pill-btn" data-testid="alt-back-to-source">
              <ArrowLeft size={12} /> Back to {source.name}
            </Link>
            <a
              className="pill-btn primary"
              href={`${API}/go/${source.slug}?ref=/alternatives/${source.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="alt-visit-source"
            >
              Visit {source.name} <ExternalLink size={12} />
            </a>
          </div>
        </div>
      </header>

      {results.length > 0 && (
        <div className="alt-summary" data-testid="alt-summary">
          <Sparkles size={12} />
          <span>
            Looking for something like <strong>{source.name}</strong>?
            {' '}These {count} tools are the closest matches we track.
          </span>
        </div>
      )}

      <div className="tool-grid" data-testid="alt-grid">
        {results.map((t, i) => (
          <div key={t.id} className="alt-card-wrap">
            <span className="alt-rank" aria-hidden data-testid={`alt-rank-${i + 1}`}>#{i + 1}</span>
            <ToolCard tool={t} />
          </div>
        ))}
      </div>

      {results.length === 0 && (
        <div className="empty-state" data-testid="alt-empty">
          No alternatives indexed yet. <Link to="/submit">Submit one</Link>.
        </div>
      )}

      <section className="alt-faq" data-testid="alt-faq">
        <h2>Why pick an alternative to {source.name}?</h2>
        <p className="muted">
          Different tools price and position themselves differently. Some are free and open-source,
          others focus on enterprise features, and a few specialize in a narrow use-case that fits your
          workflow better. Compare their pricing, feature set, and community support before committing.
        </p>
        <div className="alt-faq-cta">
          <Link to={`/category/${slugify(source.primary_category)}`} className="pill-btn">
            <Star size={12} /> Browse all {source.primary_category}
          </Link>
          <Link to="/submit" className="pill-btn">Submit a tool</Link>
        </div>
      </section>
    </div>
  );
}

function slugify(text) {
  return (text || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
}
